import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import KnowledgeBase from './components/KnowledgeBase';
import { aiService } from '../../services/aiServices';

const GiftConcierge = () => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [activeView, setActiveView] = useState('chat'); // 'chat', 'knowledge'
  const [showKnowledge, setShowKnowledge] = useState(false);
  const messagesEndRef = useRef(null);

  // Initial welcome message
  useEffect(() => {
    const welcomeMessage = {
      id: 1,
      sender: 'ai',
      content: `Hello! I'm your AI Gift Concierge, here to help you find the perfect gift. I can assist with:\n\n• Personalized gift recommendations\n• Occasion-specific suggestions\n• Budget-friendly options\n• Cultural considerations\n\nWhat kind of gift are you looking for today?`,
      timestamp: new Date(),
      quickActions: [
        { label: "Birthday Gift", action: "birthday" },
        { label: "Anniversary", action: "anniversary" },
        { label: "Housewarming", action: "housewarming" },
        { label: "Under $50", action: "budget" }
      ]
    };
    setMessages([welcomeMessage]);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (content) => {
    const userMessage = {
      id: Date.now(),
      sender: 'user',
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Use real AI API
      const context = {
        previousMessages: messages.slice(-5), // Last 5 messages for context
        timestamp: new Date().toISOString()
      };

      const aiResponse = await aiService.sendChatMessage(content, context);

      const formattedResponse = {
        id: Date.now() + 1,
        sender: 'ai',
        content: aiResponse.message || aiResponse.response || 'I apologize, but I encountered an issue. Please try again.',
        timestamp: new Date(),
        products: aiResponse.products || [],
        quickActions: aiResponse.quickActions || []
      };

      setMessages(prev => [...prev, formattedResponse]);
    } catch (error) {
      console.error('AI service error:', error);
      // Fallback to mock response if API fails
      const aiResponse = generateAIResponse(content);
      setMessages(prev => [...prev, aiResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleAttachImage = (imageData) => {
    const imageMessage = {
      id: Date.now(),
      sender: 'user',
      content: "I found this inspiration image. Can you help me find something similar?",
      image: imageData,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, imageMessage]);
    setIsTyping(true);

    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        sender: 'ai',
        content: "Great inspiration! Based on this image, I can see you're looking for something elegant and thoughtful. Let me suggest some similar options:",
        timestamp: new Date(),
        products: [
          {
            id: 1,
            name: "Artisan Ceramic Vase Set",
            description: "Hand-crafted ceramic vases with organic shapes",
            price: 89,
            rating: 4.8,
            image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=200&h=200&fit=crop"
          },
          {
            id: 2,
            name: "Minimalist Jewelry Box",
            description: "Elegant wooden jewelry organizer with velvet lining",
            price: 125,
            rating: 4.9,
            image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=200&h=200&fit=crop"
          }
        ]
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 2000);
  };

  const generateAIResponse = (userInput) => {
    const responses = {
      birthday: {
        content: "Perfect! Birthday gifts are my specialty. To give you the best recommendations, could you tell me:\n\n• Who is the birthday person? (age, relationship to you)\n• What are their interests or hobbies?\n• What's your budget range?\n• Any specific preferences or things to avoid?",
        quickActions: [
          { label: "For Mom", action: "mom_birthday" },
          { label: "For Friend", action: "friend_birthday" },
          { label: "For Partner", action: "partner_birthday" },
          { label: "For Colleague", action: "colleague_birthday" }
        ]
      },
      anniversary: {
        content: "Anniversaries are so special! I'd love to help you celebrate this milestone. Let me know:\n\n• Which anniversary year is this?\n• What does your partner love most?\n• Are you looking for something traditional or modern?\n• What's your budget?",
        products: [
          {
            id: 1,
            name: "Custom Star Map",
            description: "Personalized star map of your special date",
            price: 45,
            rating: 4.9,
            image: "https://images.unsplash.com/photo-1446776877081-d282a0f896e2?w=200&h=200&fit=crop"
          },
          {
            id: 2,
            name: "Wine Tasting Experience",
            description: "Private wine tasting for two at local vineyard",
            price: 180,
            rating: 4.8,
            image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=200&h=200&fit=crop"
          }
        ]
      },
      default: {
        content: "I'd be happy to help you find the perfect gift! Could you share more details about:\n\n• Who you're shopping for\n• The occasion or reason for the gift\n• Your budget range\n• Any specific interests they have",
        quickActions: [
          { label: "Tell me more", action: "more_info" },
          { label: "Show popular gifts", action: "popular" },
          { label: "Browse knowledge base", action: "knowledge" }
        ]
      }
    };

    const responseKey = Object.keys(responses).find(key =>
      userInput.toLowerCase().includes(key)
    ) || 'default';

    return {
      id: Date.now() + 1,
      sender: 'ai',
      ...responses[responseKey],
      timestamp: new Date()
    };
  };

  const handleQuickAction = (action) => {
    if (action.action === 'knowledge') {
      setShowKnowledge(true);
      return;
    }
    handleSendMessage(`I'm interested in ${action.label.toLowerCase()}`);
  };

  const handleProductClick = (product) => {
    const productMessage = {
      id: Date.now(),
      sender: 'ai',
      content: `Great choice! The ${product.name} is one of our most popular recommendations. Would you like me to:\n\n• Find similar alternatives\n• Check availability and shipping\n• Get more details about this product\n• Add to your wishlist for later`,
      timestamp: new Date(),
      quickActions: [
        { label: "Find similar", action: "similar" },
        { label: "Check availability", action: "availability" },
        { label: "Get details", action: "details" },
        { label: "Add to wishlist", action: "wishlist" }
      ]
    };
    setMessages(prev => [...prev, productMessage]);
  };

  const handleArticleSelect = (article) => {
    setShowKnowledge(false);
    const articleMessage = {
      id: Date.now(),
      sender: 'ai',
      content: `I see you're interested in "${article.title}". This is a comprehensive guide that covers:\n\n${article.summary}\n\nWould you like me to summarize the key points or help you apply these insights to your specific gifting situation?`,
      timestamp: new Date(),
      quickActions: [
        { label: "Summarize key points", action: "summarize" },
        { label: "Apply to my situation", action: "apply" },
        { label: "Find related articles", action: "related" }
      ]
    };
    setMessages(prev => [...prev, articleMessage]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface via-white to-primary-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center shadow-warm">
              <Icon name="MessageCircle" size={32} color="white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl lg:text-4xl text-accent-800">
                Gift Concierge
              </h1>
              <p className="text-secondary-600 font-accent text-lg">Your AI-Powered Gift Expert</p>
            </div>
          </div>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Get personalized gift recommendations from our advanced AI assistant.
            We're here to make every gift perfect through intelligent conversation and expert guidance.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-2xl p-2 shadow-sm border border-accent-200">
            <div className="flex space-x-2">
              {[
                { id: 'chat', label: 'AI Chat', icon: 'MessageCircle' },
                { id: 'knowledge', label: 'Knowledge Base', icon: 'BookOpen' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium text-sm transition-all ${activeView === tab.id
                    ? 'bg-primary text-white shadow-warm'
                    : 'text-accent-600 hover:text-accent-800 hover:bg-surface'
                    }`}
                >
                  <Icon
                    name={tab.icon}
                    size={18}
                    className={activeView === tab.id ? 'text-white' : 'text-accent-500'}
                  />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto">
          {activeView === 'chat' && (
            <div className="bg-white rounded-2xl shadow-lg border border-accent-200 overflow-hidden">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-6 py-4 border-b border-accent-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                      <Icon name="Sparkles" size={20} color="white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-accent-800">Giftsy AI Assistant</h3>
                      <p className="text-sm text-text-secondary">AI-Powered • Always Available</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="BookOpen"
                      onClick={() => setShowKnowledge(true)}
                      className="text-accent-600"
                    >
                      Knowledge Base
                    </Button>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="h-96 lg:h-[500px] overflow-y-auto p-6 space-y-4">
                {messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    onProductClick={handleProductClick}
                    onQuickAction={handleQuickAction}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <ChatInput
                onSendMessage={handleSendMessage}
                onAttachImage={handleAttachImage}
                isTyping={isTyping}
              />
            </div>
          )}

          {activeView === 'knowledge' && (
            <div className="text-center">
              <div className="bg-white rounded-2xl p-12 shadow-lg border border-accent-200">
                <Icon name="BookOpen" size={64} className="text-primary mx-auto mb-6" />
                <h2 className="font-heading font-semibold text-2xl text-accent-800 mb-4">
                  Gift Giving Knowledge Base
                </h2>
                <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
                  Access our comprehensive library of gift guides, etiquette tips, and expert insights
                  to become a master gift-giver.
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => setShowKnowledge(true)}
                  className="shadow-warm hover:shadow-primary"
                >
                  Browse Knowledge Base
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-primary mb-2">50K+</div>
            <div className="text-sm text-text-secondary">Gifts Recommended</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-secondary mb-2">98%</div>
            <div className="text-sm text-text-secondary">Satisfaction Rate</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-accent-600 mb-2">24/7</div>
            <div className="text-sm text-text-secondary">AI Availability</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-warning-500 mb-2">AI</div>
            <div className="text-sm text-text-secondary">Powered Intelligence</div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showKnowledge && (
        <KnowledgeBase
          onClose={() => setShowKnowledge(false)}
          onArticleSelect={handleArticleSelect}
        />
      )}
    </div>
  );
};

export default GiftConcierge;